/**
 * WebMCPProvider — React context that makes the WebMCP registry,
 * state provider, and (optionally) the pilot ontology manifest + persona
 * available to the component tree.
 *
 * The `ontology` and `persona` props are additive (Phase B pilot):
 * consumers that only need `registry` are unaffected.
 */

import React, { createContext, useContext, useMemo } from "react"
import type { CogoToolRegistry } from "../core/tool-registry"
import type { WebMCPStateProvider } from "../core/state-provider"

/**
 * Shape of the static WebMCP manifest emitted by
 * `apps/cogo-starter/scripts/extract-manifest.ts` (Phase B).
 *
 * Kept loose on purpose — consumers that don't set `ontology` never see
 * this type, and the starter pipeline itself still goes through the
 * ontology module for type-checked extraction.
 */
export interface WebMCPOntologyManifest {
  version?: string
  persona?: string
  narrativeContext?: string
  tools?: unknown[]
  states?: unknown[]
  actions?: unknown[]
  [key: string]: unknown
}

export interface WebMCPContextValue {
  registry: CogoToolRegistry
  stateProvider: WebMCPStateProvider | null
  /** Pilot manifest — null when not wired. */
  ontology: WebMCPOntologyManifest | null
  /** Persona id chosen by the host app. */
  persona: string | null
}

export const WebMCPContext = createContext<WebMCPContextValue | null>(null)

export interface WebMCPProviderProps {
  children: React.ReactNode
  registry: CogoToolRegistry
  stateProvider?: WebMCPStateProvider | null
  ontology?: WebMCPOntologyManifest | null
  persona?: string | null
}

export function WebMCPProvider({
  children,
  registry,
  stateProvider = null,
  ontology = null,
  persona = null,
}: WebMCPProviderProps) {
  const value = useMemo<WebMCPContextValue>(
    () => ({ registry, stateProvider, ontology, persona }),
    [registry, stateProvider, ontology, persona],
  )
  return (
    <WebMCPContext.Provider value={value}>
      {children}
    </WebMCPContext.Provider>
  )
}

/**
 * Internal helper — retrieve the WebMCP context or throw if missing.
 */
export function useWebMCPContext(): WebMCPContextValue {
  const ctx = useContext(WebMCPContext)
  if (!ctx) {
    throw new Error(
      "useWebMCPContext must be used within a <WebMCPProvider>. " +
      "Wrap your component tree with <WebMCPProvider registry={...}>."
    )
  }
  return ctx
}
